
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { AppointmentDetails } from "./appointment-details"

// Mock data for appointments
const appointments = [
  {
    id: "1",
    title: "Personal Training Session",
    date: new Date(2023, 4, 15, 10, 0),
    endDate: new Date(2023, 4, 15, 11, 0),
    clientId: "1",
    clientName: "John Smith",
    trainerId: "1",
    trainerName: "Mike Johnson",
    status: "confirmed",
    notes: "Focus on upper body strength",
  },
  {
    id: "2",
    title: "Yoga Session",
    date: new Date(2023, 4, 16, 14, 0),
    endDate: new Date(2023, 4, 16, 15, 0),
    clientId: "2",
    clientName: "Sarah Williams",
    trainerId: "2",
    trainerName: "Jane Smith",
    status: "confirmed",
    notes: "Beginner level session",
  },
  {
    id: "3",
    title: "CrossFit Training",
    date: new Date(2023, 4, 17, 9, 0),
    endDate: new Date(2023, 4, 17, 10, 30),
    clientId: "3",
    clientName: "David Brown",
    trainerId: "5",
    trainerName: "David Brown",
    status: "confirmed",
    notes: "High intensity workout",
  },
  {
    id: "4",
    title: "Pilates Session",
    date: new Date(2023, 4, 18, 16, 0),
    endDate: new Date(2023, 4, 18, 17, 0),
    clientId: "4",
    clientName: "Emily Johnson",
    trainerId: "4",
    trainerName: "Sarah Williams",
    status: "pending",
    notes: "Focus on core strength",
  },
  {
    id: "5",
    title: "Weight Training",
    date: new Date(2023, 4, 19, 11, 0),
    endDate: new Date(2023, 4, 19, 12, 0),
    clientId: "5",
    clientName: "Michael Wilson",
    trainerId: "1",
    trainerName: "Mike Johnson",
    status: "confirmed",
    notes: "Lower body focus",
  },
]

export function AppointmentCalendar() {
  const { t } = useTranslation()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null)

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  })

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((appointment) => isSameDay(appointment.date, date))
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>{format(currentMonth, "MMMM yyyy")}</CardTitle>
            <CardDescription>{t("appointments.calendarDescription")}</CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2 text-center">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="font-medium text-sm py-2">
                {day}
              </div>
            ))}
            {Array.from({ length: startOfMonth(currentMonth).getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="h-24 border rounded-md bg-muted/20" />
            ))}
            {daysInMonth.map((day) => {
              const dayAppointments = getAppointmentsForDate(day)
              const hasAppointments = dayAppointments.length > 0
              return (
                <div
                  key={day.toString()}
                  className={cn(
                    "h-24 border rounded-md p-1 overflow-hidden",
                    hasAppointments ? "bg-primary/5" : "",
                    selectedDate && isSameDay(day, selectedDate) ? "ring-2 ring-primary" : "",
                  )}
                  onClick={() => {
                    setSelectedDate(day)
                    setSelectedAppointment(null)
                  }}
                >
                  <div className="font-medium text-sm">{format(day, "d")}</div>
                  <div className="space-y-1 mt-1">
                    {dayAppointments.slice(0, 2).map((appointment) => (
                      <div
                        key={appointment.id}
                        className={cn(
                          "text-xs truncate rounded px-1 py-0.5",
                          appointment.status === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800",
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDate(day)
                          setSelectedAppointment(appointment)
                        }}
                      >
                        {format(appointment.date, "HH:mm")} - {appointment.title}
                      </div>
                    ))}
                    {dayAppointments.length > 2 && (
                      <div className="text-xs text-muted-foreground">+{dayAppointments.length - 2} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {selectedDate && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t("appointments.appointmentsFor")} {format(selectedDate, "MMMM d, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getAppointmentsForDate(selectedDate).length > 0 ? (
              <div className="space-y-4">
                {getAppointmentsForDate(selectedDate).map((appointment) => (
                  <div
                    key={appointment.id}
                    className={cn(
                      "p-3 rounded-md border cursor-pointer",
                      selectedAppointment?.id === appointment.id ? "ring-2 ring-primary" : "hover:bg-accent",
                    )}
                    onClick={() => setSelectedAppointment(appointment)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{appointment.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {format(appointment.date, "HH:mm")} - {format(appointment.endDate, "HH:mm")}
                        </p>
                      </div>
                      <div
                        className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          appointment.status === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800",
                        )}
                      >
                        {appointment.status === "confirmed" ? t("appointments.confirmed") : t("appointments.pending")}
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm">
                        <span className="font-medium">{t("appointments.client")}:</span> {appointment.clientName}
                      </p>
                      <p className="text-sm">
                        <span className="font-medium">{t("appointments.trainer")}:</span> {appointment.trainerName}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">{t("appointments.noAppointments")}</div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedAppointment && (
        <AppointmentDetails appointment={selectedAppointment} onClose={() => setSelectedAppointment(null)} />
      )}
    </div>
  )
}
